import { HistoricProps } from '@/components/table-historic-prices/table-historic-prices.component';
import { ModelsSelectProps, SelectOptionsProps } from '@/interfaces/filters';
import { brandsProps } from '@/interfaces/pages';
import { AveragesPrices } from '@/interfaces/result';
import {
    BRANDS_MAIN_BIKE_WITH_MODELS,
    BRANDS_MAIN_CAR_WITH_MODELS,
    PRIORITY_ORDER_BIKE,
    PRIORITY_ORDER_CAR,
    TYPE_BIKE,
    TYPE_CAR,
} from './constants';

export function capitalize(val: string) {
    if (typeof val !== 'string' || val.trim().length === 0) return '';

    const words = val.split(' ');
    const capitalizedWords = words?.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });

    return capitalizedWords.join(' ');
}

export function formatToPrice(value: number | string | null | undefined) {
    if (typeof value === 'string') {
        value = parseFloat(value);
    }

    if (value === null || value === undefined || isNaN(value)) {
        return '-';
    }

    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatToSlug(value: string) {
    if (value) {
        // Remove acentos e caracteres especiais
        value = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        // Remove pontos
        value = value.replace(/\./g, '');

        // Remove todos os caracteres que não são letras, números, hífens ou espaços
        value = value.replace(/[^\w\s-]/g, '');

        // Substitui espaços por hífens
        value = value.trim().replace(/\s+/g, '-');

        return value.toLowerCase();
    }
    return null;
}

export function filterMainBrandsCar(brands: brandsProps[]) {
    const priorityMap = new Map(PRIORITY_ORDER_CAR.map((name, index) => [name.toLowerCase(), index]));

    const priorityBrands = brands?.filter((brand) => priorityMap.has(brand.name.toLowerCase()));

    priorityBrands?.sort((a, b) => {
        const aIndex = priorityMap.get(a.name.toLowerCase());
        const bIndex = priorityMap.get(b.name.toLowerCase());

        return Number(aIndex) - Number(bIndex);
    });

    return [...priorityBrands, ...brands];
}

export function filterMainBrandsBike(brands: brandsProps[]) {
    const priorityMap = new Map(PRIORITY_ORDER_BIKE.map((name, index) => [name.toLowerCase(), index]));

    const priorityBrands = brands?.filter((brand) => priorityMap.has(brand.name.toLowerCase()));

    priorityBrands?.sort((a, b) => {
        const aIndex = priorityMap.get(a.name.toLowerCase());
        const bIndex = priorityMap.get(b.name.toLowerCase());
        return Number(aIndex) - Number(bIndex);
    });

    return [...priorityBrands, ...brands];
}

export function getModelsByMainBrand(vehicleType: number, brand: string): string[] {
    if (!brand) return [];

    const models =
        vehicleType === TYPE_CAR
            ? BRANDS_MAIN_CAR_WITH_MODELS[capitalize(brand)] || []
            : BRANDS_MAIN_BIKE_WITH_MODELS[capitalize(brand)] || [];

    return models.sort((previousModel: string, nextModel: string): number => previousModel.localeCompare(nextModel));
}

export function orderByMainModels(priorityOrder: string[], models: ModelsSelectProps[]) {
    const priorityMap = new Map(priorityOrder.map((name, index) => [name.toLowerCase(), index]));

    const priorityModels = models?.filter((model) => priorityMap.has(model.name.toLowerCase()));

    priorityModels?.sort((a, b) => {
        const aIndex = priorityMap.get(a.name.toLowerCase());
        const bIndex = priorityMap.get(b.name.toLowerCase());
        return Number(aIndex) - Number(bIndex);
    });

    return [...priorityModels, ...models];
}

export function reorganizeModels(models: SelectOptionsProps[]): SelectOptionsProps[][] {
    if (!models) return [];
    models?.sort((a, b) => a.label.localeCompare(b.label));
    const qtdColumns = 4;
    const columns: SelectOptionsProps[][] = Array.from({ length: qtdColumns }, () => []);

    let total = models.length;

    const initialColumns = qtdColumns;

    let totalColumns = initialColumns;

    for (let i = 0; i < initialColumns; i++) {
        const totalLines = Math.ceil(total / totalColumns);

        if (columns[i].length < totalLines) {
            const totalAvailable = totalLines - columns[i].length;

            columns[i] = models.splice(0, totalAvailable);

            total -= totalAvailable;

            totalColumns--;
        }
    }

    return columns;
}

export function formatOriginalOptions(data: ModelsSelectProps[]) {
    return data.map((model: ModelsSelectProps) => ({
        value: model.slugged,
        label: capitalize(model.name),
    }));
}

export function formatFormattedModels(listModels: ModelsSelectProps[]) {
    return listModels.map((item: ModelsSelectProps) => ({
        value: item.slugged,
        label: capitalize(item.name),
    }));
}

export function formatModelDetails(data: ModelsSelectProps[]) {
    return data.map((model: ModelsSelectProps) => ({
        id: model.id,
        label: capitalize(model.name),
        brand_id: model.brand_id,
        image_path: model.image_path,
    }));
}

/**
 * Retorna o tipo de veículo em número.
 * @param {string} vehicleType - Tipo do veículo ('carros' ou 'motos')
 * @returns {number} 0 para carros e 1 para motos
 */
export function getVehicleTypeInNumber(vehicleType: string) {
    return vehicleType === 'carros' ? TYPE_CAR : TYPE_BIKE;
}
export function formatVersionCarTitle(version: string) {
    return capitalize(
        decodeURI(version)
            .replace(/-/g, ' ')
            .replace(/^(\d)(\d)/, '$1.$2'),
    );
}

export function formatModelCar(model: string) {
    return model
        .replace(/-/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Retorna um objeto mapeado com o contrato de atributo da solução
 * @param {string | number} attribute - Valor do atributo do veiculo ex. marca, model... do veiculo
 * @returns {{label: string; value: string}} valor e label baseado no atributo selecionado
 */
export const mountVehicleAttributes = (attribute: string) => ({ label: attribute, value: attribute });

export const handleWebmotorsPrice = (prices: AveragesPrices['wm'], isVehicleArmored: boolean) => {
    try {
        const isStateArmored = prices?.state_price?.map(({ armored }) => armored)?.includes(true);
        const isBrazilianArmored = prices?.brazilian_price?.map(({ armored }) => armored)?.includes(true);

        const brazilianMediumPrice: (string | number)[] =
            prices?.brazilian_price
                ?.filter(({ armored }) => (isVehicleArmored && armored) || (!isVehicleArmored && !armored))
                .map(({ medium_price }) => medium_price) || [];

        const stateMediumPriceArmored: (string | number)[] =
            prices?.state_price
                ?.filter(({ armored }) => isVehicleArmored && armored)
                .map(({ medium_price }) => medium_price) || [];

        const stateMediumPrice: (string | number)[] =
            prices?.state_price?.filter(({ armored }) => !armored).map(({ medium_price }) => medium_price) || [];

        const hasArmored = isStateArmored || isBrazilianArmored;

        const isBrazilianPrice =
            (isVehicleArmored && !isStateArmored && isBrazilianArmored) ||
            ((!stateMediumPrice || stateMediumPrice?.length === 0) && brazilianMediumPrice?.length > 0);

        return {
            isBrazilianPrice,
            brazilianMediumPrice,
            stateMediumPriceArmored,
            stateMediumPrice,
            hasArmored,
            isStateArmored,
            isBrazilianArmored,
        };
    } catch (error) {
        console.log('Error in handleWebmotorsPrice, não possui valores:', error);

        return {
            isBrazilianPrice: false,
            brazilianMediumPrice: [],
            stateMediumPriceArmored: [],
            stateMediumPrice: [],
            hasArmored: false,
            isStateArmored: false,
            isBrazilianArmored: false,
        };
    }
};

/**
 * Retorna a imagem padrão de um veículo com base no tipo (carro ou moto).
 *
 * @param {boolean} isCar - Indica se o veículo é um carro (true) ou uma moto (false).
 * @param {string} [imagePath] - Caminho da imagem personalizada (opcional).
 * @returns {string} - Caminho da imagem padrão ou personalizada.
 *
 * @remarks
 * - Se `isCar` for `false` (moto), a função sempre retornará a imagem padrão de moto.
 * - Se `isCar` for `true` (carro) e `imagePath` for fornecido, retornará a imagem personalizada; caso contrário, retornará a imagem padrão de carro.
 */
export function getDefaultImage(isCar: boolean, imagePath?: string): string {
    const defaultImage = isCar
        ? `${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/car_default-2.svg`
        : `${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/bike-default-2.svg`;

    return !isCar ? defaultImage : imagePath || defaultImage;
}

export const getVersionSlugged = (version: string) => {
    let versionSlug = version;
    if (version.length >= 2 && /\d/.test(version[0]) && /\d/.test(version[1])) {
        versionSlug = version[0] + '-' + version.substring(1);
    }
    return versionSlug;
};

/**
 * Verifica se o valor fornecido corresponde ao tipo de veículo especificado.
 *
 * @param {string | number} value - O valor a ser verificado. Pode ser uma string (ex.: "carros", "motos") ou um número (ex.: TYPE_CAR, TYPE_BIKE).
 * @param {'car' | 'bike'} typeToCheck - O tipo de veículo a ser comparado. Pode ser 'car' (carro) ou 'bike' (moto).
 * @returns {boolean} - Retorna `true` se o valor corresponder ao tipo de veículo especificado, caso contrário, retorna `false`.
 *
 * @example
 * // Comparação com string
 * isVehicleType('carros', 'car'); // Retorna true
 * isVehicleType('motos', 'bike'); // Retorna true
 *
 * @example
 * // Comparação com número
 * isVehicleType(TYPE_CAR, 'car'); // Retorna true
 * isVehicleType(TYPE_BIKE, 'bike'); // Retorna true
 *
 * @example
 * // Comparação inválida
 * isVehicleType('avião', 'car'); // Retorna false
 * isVehicleType(999, 'bike'); // Retorna false
 */
export function isVehicleType(value: string | number, typeToCheck: 'car' | 'bike'): boolean {
    if (typeof value === 'string') {
        if (typeToCheck === 'car') return value === 'carros' || value === 'car';
        if (typeToCheck === 'bike') return value === 'motos' || value === 'bike';
    } else if (typeof value === 'number') {
        if (typeToCheck === 'car') return value === TYPE_CAR;
        if (typeToCheck === 'bike') return value === TYPE_BIKE;
    }
    return false;
}

/**
 * Formata um objeto Date para o padrão "Abv/AAAA" (ex: "Abr/2025") em português do Brasil.
 *
 * - O mês é abreviado, com a primeira letra maiúscula e sem ponto final.
 * - O ano é exibido completo.
 * - Se a data for inválida, lança um erro.
 *
 * @param {Date} date - A data a ser formatada.
 * @returns {string} A data formatada no padrão "Abv/AAAA".
 * @throws {Error} Se a data fornecida for inválida.
 */
export function formatMonthYear(date: Date): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error('Invalid date provided');
    }
    const formatted = date
        .toLocaleDateString('pt-BR', { month: 'short', year: 'numeric', timeZone: 'UTC' })
        .replace('.', '') // remove o ponto do mês
        .replace(' de ', '/')
        .replace(/^./, (str) => str.toUpperCase()); // primeira letra maiúscula
    return formatted;
}

const parseMonthYear = (str: string) => {
    const [month, year] = str.split('/');
    const months = [
        'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
        'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];
    const idx = months.findIndex(m => m === month);
    return new Date(Number(year), idx !== -1 ? idx : 0, 1);
};

const mergeWmAverage = (
    wmData: { ref_month: string; last_price: number; price_percent_variation: number | null }[],
    map: Map<string, {
        ref_month: string;
        wm_last_price?: number | string | null;
        wm_price_percent_variation?: number | null;
    }>,
) => {
    wmData?.forEach(item => {
        const key = item.ref_month;
        const formattedMonth = formatMonthYear(new Date(item.ref_month));
        if (map.has(key)) {
            const existing = map.get(key)!;
            map.set(key, {
                ...existing,
                wm_last_price: formatToPrice(item.last_price),
                wm_price_percent_variation: item.price_percent_variation,
            });
        } else {
            map.set(key, {
                ref_month: formattedMonth,
                wm_last_price: formatToPrice(item.last_price),
                wm_price_percent_variation: item.price_percent_variation,
            });
        }
    });
}

const addFipeToMap = (
    fipeData: { ref_month: string; last_price: number; price_percent_variation: number | null }[] | undefined,
    map: Map<string, {
        ref_month: string;
        fipe_last_price?: number | string | null;
        fipe_price_percent_variation?: number | null;
    }>
) => {
    fipeData?.forEach(item => {
        const refMonth = formatMonthYear(new Date(item.ref_month));
        map.set(item.ref_month, {
            ref_month: refMonth,
            fipe_last_price: formatToPrice(item.last_price),
            fipe_price_percent_variation: item.price_percent_variation,
        });
    });
};


/**
 * Cria um array formatado e ordenado de dados históricos de preços de veículos, combinando médias da FIPE e Webmotors (WM).
 *
 * Esta função mescla e formata os dados históricos de preços das fontes FIPE e WM, tratando veículos blindados e não blindados,
 * assim como veículos novos e usados. O array resultante é ordenado pelo mês de referência em ordem decrescente (mais recente primeiro),
 * e garante que o mês atual (se presente) esteja sempre no início do array.
 *
 * @param fipe - Os dados históricos da FIPE, contendo arrays de preços 'new' (novos) ou 'used' (usados).
 * @param wm_average - Os dados históricos de médias da Webmotors, contendo arrays de preços 'armored' (blindados) e 'not_armored' (não blindados).
 * @param isVehicle0km - Indica se o veículo é novo (0km). Se true, utiliza os dados 'new' da FIPE; caso contrário, utiliza 'used'.
 * @param isVehicleArmored - Indica se o veículo é blindado. Se true, utiliza os dados 'armored' da WM; caso contrário, utiliza 'not_armored'.
 * @returns Um array de objetos representando os dados históricos mesclados e formatados, ordenados pelo mês de referência (mais recente primeiro),
 *          com o mês atual (se presente) no início do array.
 */


export const createMapHistoric = (
    fipe: HistoricProps['historic']['fipe'],
    wm_average: HistoricProps['historic']['wm_average'],
    isVehicle0km: boolean, isVehicleArmored: boolean
) => {
    const map = new Map<string, {
        ref_month: string;
        fipe_last_price?: number | string | null;
        fipe_price_percent_variation?: number | null;
        wm_last_price?: number | string | null;
        wm_price_percent_variation?: number | null;
    }>();

    if (!isVehicle0km && !isVehicleArmored) {
        addFipeToMap(fipe?.used, map);
        mergeWmAverage(wm_average?.not_armored, map);
    }

    if (isVehicle0km) {
        addFipeToMap(fipe?.new, map);
        mergeWmAverage([], map);
    }
    
    if (isVehicleArmored) {
        addFipeToMap([], map);
        mergeWmAverage(wm_average?.armored, map);
    }

    let mapFormatted = Array.from(map.values()).sort(
        (a, b) => {
            return parseMonthYear(b.ref_month).getTime() - parseMonthYear(a.ref_month).getTime();
        }
    );

    const now = new Date();
    const currentMonth = formatMonthYear(now);
    const currentIdx = mapFormatted.findIndex(item => item.ref_month === currentMonth);
    if (currentIdx > 0) {
        const [current] = mapFormatted.splice(currentIdx, 1);
        mapFormatted.unshift(current);
    }

    return mapFormatted;
};


export const checkIfHasData = (isVehicleArmored, isVehicle0km, historic) => {
    if ((!isVehicle0km && historic?.fipe?.used?.length > 0) || 
        (!isVehicleArmored && historic?.wm_average?.not_armored?.length > 0)) return true;

    if (isVehicle0km && !historic?.fipe?.new) return false;
    if (isVehicleArmored && !historic?.wm_average?.armored) return false;
}

export const formatPercentVariation = (value: number | null) => {
    if (value === null || value === undefined) return '-';
    if (value === 0) return `${value}%`;
    return (value) < 0 ? `${(value).toFixed(2).replace('.', ',')}%` : `+${(value).toFixed(2).replace('.', ',')}%`;
};